import { useEffect, useState } from 'react';
import { useOutletContext, Link } from 'react-router-dom';
import type {
  ApiClient, ProjektDetail as Detail, Modell, Exportgruppe, ProfilEintrag, Sandboxmodell,
} from '../api';
import {
  IconModell, IconPlus, IconIfc, IconPdf, IconDwg, IconTuer, IconTuertyp,
  IconRaumLesen, IconRaumSchreiben, IconHerunterladen,
  IconLueftung, IconKennung,
} from '../Icons';
import { ApiFehler, FEHLER_REVIT_VERSION_OHNE_MASCHINE, auftragZustand } from '../api';
import { auftragBeendet, auftragStatusName, auftragStatusText } from '../auftragsstatus';
import { ART_IFC_AUS_DATENKERN, exportaktionen } from '../ifcDatenkern';
import { ART_ENGINE_AUFNAHME, engineAktion, engineHindernis } from '../engineEingang';
import type { Rahmen } from './Projektrahmen';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, DataTable, Drawer, EmptyState, ErrorState, Feld, FormSection,
  LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Reiter „Uebersicht": Stammdaten und Modelle.
 *
 * Was hier steht, betrifft das Projekt als Ganzes und die Modelle darin -
 * anlegen, stilllegen, Auftraege ausloesen, bisherige Exporte einsehen.
 *
 * Die fachbezogenen Bestaende liegen in eigenen Reitern, die Zuordnung der
 * Modelle zu den Fachbereichen unter „Einstellungen". Bis zur Aufteilung stand
 * beides auf einer Seite und war bei neun Gewerken nicht mehr auffindbar.
 */
export function Projektuebersicht() {
  const { api, projekt, neuLaden } = useOutletContext<Rahmen>();
  const [stammdatenBearbeiten, setStammdatenBearbeiten] = useState(false);

  if (!projekt) {
    return (
      <>
        <PageHeader
          titel="Übersicht"
          beschreibung="Stammdaten und Modelle des Projekts."
        />
        <LoadingState text="Projekt wird geladen …" zeilen={6} spalten={4} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        titel={`${projekt.number} — ${projekt.name}`}
        beschreibung={'Was hier steht, betrifft das Projekt als Ganzes und die Modelle '
          + 'darin: anlegen, stilllegen, Aufträge auslösen, bisherige Exporte einsehen. '
          + 'Die fachbezogenen Bestände liegen in eigenen Reitern.'}
        meta={(
          <>
            {projekt.accRegion && <>ACC {projekt.accRegion} · </>}
            {projekt.modelle.filter((m) => m.isActive).length} aktive Modelle
          </>
        )}
        aktionen={!stammdatenBearbeiten && (
          <button className="m-knopf" onClick={() => setStammdatenBearbeiten(true)}>
            Stammdaten bearbeiten
          </button>
        )}
      />

      {/* Der Lesezustand der Stammdaten steht seit U6a IM SEITENKOPF (Nummer,
          Name, Region als Titel und Metazeile) — eine Karte, die dasselbe
          noch einmal sagt, ist Rauschen (Abschnitt 25). Übrig bleibt das
          Formular. */}
      {stammdatenBearbeiten && (
        <Stammdaten
          api={api}
          projekt={projekt}
          neuLaden={neuLaden}
          fertig={() => setStammdatenBearbeiten(false)}
        />
      )}
      <Modelle api={api} projekt={projekt} neuLaden={neuLaden} />
      <SandboxModelle api={api} projekt={projekt} />
    </>
  );
}

function Stammdaten({
  api, projekt, neuLaden, fertig,
}: {
  api: ApiClient;
  projekt: Detail;
  neuLaden: () => void;
  fertig: () => void;
}) {
  const [name, setName] = useState(projekt.name);
  const [region, setRegion] = useState(projekt.accRegion ?? 'EMEA');
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    setName(projekt.name);
    setRegion(projekt.accRegion ?? 'EMEA');
  }, [projekt]);

  const speichern = async () => {
    setFehler(null);
    try {
      await api.projektAendern(projekt.id, { name: name.trim(), accRegion: region });
      fertig();
      neuLaden();
    } catch (e) {
      setFehler(e);
    }
  };

  return (
    <FormSection
      titel="Stammdaten"
      spalten={2}
      fuss={(
        <>
          <button className="m-knopf" onClick={fertig}>
            Abbrechen
          </button>
          <button className="m-knopf m-knopf-haupt" onClick={speichern}
                  disabled={!name.trim()}>
            Speichern
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Die Stammdaten konnten nicht gespeichert werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Projektnummer"
        wert={projekt.number}
        nurLesen
        hinweis="Nicht änderbar — Aufträge und Dateipfade beziehen sich darauf."
      />
      <Feld
        beschriftung="Name"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        pflicht
      />
      <Feld
        art="auswahl"
        beschriftung="ACC-Region"
        wert={region}
        beiAenderung={(w) => setRegion(w as string)}
        optionen={[
          { wert: 'EMEA', beschriftung: 'EMEA' },
          { wert: 'US', beschriftung: 'US' },
        ]}
      />
    </FormSection>
  );
}

// --- Konfigurationsdateien --------------------------------------------------


function Modelle({
  api, projekt, neuLaden,
}: {
  api: ApiClient;
  projekt: Detail;
  neuLaden: () => void;
}) {
  const [anlegen, setAnlegen] = useState(false);
  const [stilllegen, setStilllegen] = useState<Modell | null>(null);
  /** Die Exporte eines Modells im Schubfach (Abschnitt 16). */
  const [exporteVon, setExporteVon] = useState<Modell | null>(null);

  const aktive = projekt.modelle.filter((m) => m.isActive);
  const stillgelegte = projekt.modelle.filter((m) => !m.isActive);

  const spalten: Spalte<Modell>[] = [
    {
      schluessel: 'name', titel: 'Name', immerSichtbar: true,
      wert: (m) => m.name,
      inhalt: (m) => <strong>{m.name}</strong>,
    },
    {
      schluessel: 'gewerk', titel: 'Gewerk', breite: '180px', sortierbar: false,
      wert: (m) => m.discipline ?? '',
      inhalt: (m) => (
        <Gewerkfeld api={api} projektId={projekt.id} modell={m} neuLaden={neuLaden} />
      ),
    },
    {
      schluessel: 'revit', titel: 'Revit', breite: '100px',
      wert: (m) => m.revitVersion,
      inhalt: (m) => <span className="schwach">{m.revitVersion}</span>,
    },
    {
      schluessel: 'limit', titel: 'Limit', einheit: 'min', ausrichtung: 'rechts',
      breite: '110px', sortierbar: false,
      titelHinweis: 'Zeitlimit für die Verarbeitung',
      wert: (m) => m.timeoutMinutes ?? 90,
      inhalt: (m) => (
        <Zeitlimitfeld api={api} projektId={projekt.id} modell={m} neuLaden={neuLaden} />
      ),
    },
    {
      schluessel: 'herkunft', titel: 'Herkunft', art: 'status', breite: '120px',
      wert: (m) => (m.source === 2 ? 'ACC' : 'Datei'),
      inhalt: (m) => (m.source === 2
        ? <StatusBadge zustand="info" text="ACC" klein />
        : <StatusBadge zustand="neutral" text="Datei" klein />),
    },
    {
      schluessel: 'beauftragen', titel: 'Beauftragen', sortierbar: false,
      ausrichtung: 'mitte', breite: '230px',
      inhalt: (m) => (
        <Modellaktionen
          api={api} projektId={projekt.id} modell={m} ifcAusDatenkern={projekt.ifcAusDatenkern}
          engineEingang={projekt.engineEingang}
        />
      ),
    },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, ausrichtung: 'rechts',
      breite: '200px',
      inhalt: (m) => (
        <span style={{ whiteSpace: 'nowrap' }}>
          <button
            className="m-knopf"
            title="Bisherige Exporte anzeigen"
            onClick={(e) => { e.stopPropagation(); setExporteVon(m); }}
          >
            Exporte
          </button>{' '}
          <button
            className="m-knopf"
            onClick={(e) => { e.stopPropagation(); setStilllegen(m); }}
          >
            Stilllegen
          </button>
        </span>
      ),
    },
  ];

  return (
    <>
      {anlegen && (
        <ModellAnlegen
          api={api}
          projektId={projekt.id}
          fertig={() => { setAnlegen(false); neuLaden(); }}
        />
      )}

      <DataTable
        name="Modelle"
        spalten={spalten}
        zeilen={aktive}
        schluessel={(m) => m.id}
        suche={aktive.length > 8}
        ausfuhr
        werkzeuge={(
          <button className="m-knopf" onClick={() => setAnlegen(!anlegen)}>
            {anlegen ? 'Abbrechen' : <><IconPlus size={16} />Modell anlegen</>}
          </button>
        )}
        leer={(
          <EmptyState
            icon={<IconModell />}
            titel="Noch keine Modelle."
            erklaerung={'Ein Modell verbindet die Plattform mit einer Revit-Datei — aus '
              + 'ACC oder als Datei. Ohne Modell lässt sich kein Auftrag auslösen.'}
            aktion={(
              <button className="m-knopf m-knopf-haupt" onClick={() => setAnlegen(true)}>
                <IconPlus size={16} />Modell anlegen
              </button>
            )}
          />
        )}
      />

      {stillgelegte.length > 0 && (
        <div className="karte">
          <p className="sehr-schwach" style={{ margin: '0 0 8px' }}>
            Stillgelegt ({stillgelegte.length}) — die Aufträge dazu bleiben erhalten:
          </p>

          {stillgelegte.map((m) => (
            <div key={m.id} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '4px 0', fontSize: 13,
            }}>
              <span className="schwach">{m.name}</span>
              <span className="sehr-schwach">{m.discipline ?? ''}</span>
              <button
                className="m-knopf"
                style={{ marginLeft: 'auto' }}
                onClick={async () => {
                  await api.modellAendern(projekt.id, m.id, { isActive: true });
                  neuLaden();
                }}
              >
                Wieder aufnehmen
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Die Exporte stehen im SCHUBFACH und nicht mehr als aufklappbare
          Zeile in der Tabelle: `DataTable` virtualisiert über eine feste
          Zeilenhöhe, und ein Baum, der sich in einer Zeile entfaltet, hat
          keine. Im Schubfach hat er zudem die ganze Breite. */}
      <Drawer
        offen={exporteVon !== null}
        titel={exporteVon ? `${exporteVon.name} — bisherige Exporte` : ''}
        beschreibung="Nach Art gegliedert; der jüngste erfolgreiche Lauf ist gekennzeichnet."
        breite="860px"
        beiSchliessen={() => setExporteVon(null)}
      >
        {exporteVon && (
          <Exportbaum api={api} projektId={projekt.id} modell={exporteVon} />
        )}
      </Drawer>

      <StilllegenDialog
        modell={stilllegen}
        abbrechen={() => setStilllegen(null)}
        bestaetigen={async () => {
          if (!stilllegen) return;
          await api.modellStilllegen(projekt.id, stilllegen.id);
          setStilllegen(null);
          neuLaden();
        }}
      />
    </>
  );
}

/**
 * Die Sandbox-Kopien des Projekts: von welchem Modell sie stammen, wie alt
 * sie sind und ob sich das ueberhaupt zuverlaessig sagen laesst.
 *
 * Wird am ACC-Modell weitergearbeitet, bleibt die Sandbox-Kopie ein
 * eingefrorener Stand - die KI-gestuetzte Modellvervollstaendigung plant
 * dann auf veralteten Daten, ohne dass das hier sichtbar waere. Deshalb
 * zeigt diese Karte beide Zeitpunkte (Kopie erzeugt / Quelle zuletzt
 * aufgenommen) und behauptet "aktuell" nur, wenn dafuer ein Beleg da ist.
 */
function SandboxModelle({ api, projekt }: { api: ApiClient; projekt: Detail }) {
  const [kopien, setKopien] = useState<Sandboxmodell[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);

  useEffect(() => {
    api.sandboxmodelle(projekt.id).then(setKopien).catch(setFehler);
  }, [api, projekt.id]);

  const spalten: Spalte<Sandboxmodell>[] = [
    {
      schluessel: 'name', titel: 'Name', immerSichtbar: true,
      wert: (k) => k.name,
      inhalt: (k) => <strong>{k.name}</strong>,
    },
    {
      schluessel: 'quelle', titel: 'Quellmodell',
      wert: (k) => (k.quellmodell.vorhanden ? k.quellmodell.name : ''),
      inhalt: (k) => (k.quellmodell.vorhanden
        ? <span className="schwach">{k.quellmodell.name}</span>
        : <span className="sehr-schwach">nicht mehr vorhanden</span>),
    },
    {
      schluessel: 'gewerk', titel: 'Gewerk', breite: '130px',
      wert: (k) => k.discipline ?? '',
      inhalt: (k) => <span className="schwach">{k.discipline ?? '—'}</span>,
    },
    {
      schluessel: 'stand', titel: 'Stand', art: 'zahl', breite: '100px',
      wert: (k) => k.sandboxStand,
    },
    {
      schluessel: 'erzeugt', titel: 'Erzeugt am', art: 'datum', breite: '160px',
      wert: (k) => k.sandboxErzeugtAm ?? '',
      inhalt: (k) => (
        <span className="schwach">
          {k.sandboxErzeugtAm
            ? new Date(k.sandboxErzeugtAm).toLocaleString('de-DE', {
              day: '2-digit', month: '2-digit', year: '2-digit',
              hour: '2-digit', minute: '2-digit',
            })
            : '—'}
        </span>
      ),
    },
    {
      schluessel: 'hash', titel: 'Hash', breite: '150px', sortierbar: false,
      wert: (k) => k.sandboxQuellHash ?? '',
      inhalt: (k) => (
        <span className="sehr-schwach"
              style={{ fontFamily: 'monospace', fontSize: 11 }}
              title={k.sandboxQuellHash ?? undefined}>
          {k.sandboxQuellHash ? `${k.sandboxQuellHash.slice(0, 12)}…` : '—'}
        </span>
      ),
    },
    {
      schluessel: 'beschreibbar', titel: 'Beschreibbar', art: 'status', breite: '150px',
      wert: (k) => (k.darfAlsSandboxBeschriebenWerden ? 'beschreibbar' : 'gesperrt'),
      inhalt: (k) => (k.darfAlsSandboxBeschriebenWerden
        ? <StatusBadge zustand="gut" text="beschreibbar" klein />
        : <StatusBadge zustand="neutral" text="gesperrt" klein />),
    },
    {
      schluessel: 'alter', titel: 'Alter', art: 'status', breite: '140px',
      wert: (k) => (k.istNachweislichVeraltet ? 'veraltet' : 'unbekannt'),
      inhalt: (k) => (k.istNachweislichVeraltet
        ? (
          <StatusBadge
            zustand="fehler" text="veraltet" klein
            titel="Das Quellmodell wurde nach der Sandbox-Erzeugung neu aufgenommen."
          />
        )
        : (
          <StatusBadge
            zustand="neutral" text="unbekannt" klein
            titel={'Ob am ACC-Modell seither weitergearbeitet wurde, lässt sich hier '
              + 'nicht feststellen.'}
          />
        )),
    },
  ];

  return (
    <DataTable
      name="Sandbox-Modelle"
      beschreibung={'Wird am ACC-Modell weitergearbeitet, bleibt die Sandbox-Kopie ein '
        + 'eingefrorener Stand. Deshalb stehen hier beide Zeitpunkte — und „aktuell" '
        + 'wird nur behauptet, wenn es dafür einen Beleg gibt.'}
      spalten={spalten}
      zeilen={kopien ?? []}
      schluessel={(k) => k.id}
      laedt={kopien === null && fehler === null}
      fehler={fehler}
      ausfuhr
      leer={(
        <EmptyState
          icon={<IconModell />}
          titel="Noch keine Sandbox-Modelle."
          erklaerung={'Eine Sandbox-Kopie entsteht, wenn die KI-gestützte '
            + 'Modellvervollständigung auf einem eingefrorenen Stand arbeiten soll.'}
        />
      )}
    />
  );
}

/**
 * Fragt vor dem Stilllegen nach - mit Eingabe des Modellnamens.
 *
 * Ein einfaches Bestätigen wäre zu schnell weggeklickt. Wer den Namen tippen
 * muss, hat gelesen, um welches Modell es geht - und das ist bei fünf ähnlich
 * benannten Fachmodellen der Punkt, auf den es ankommt.
 *
 * Rückgängig geht es über "Wieder aufnehmen" - das steht auch im Dialog, damit
 * niemand aus Sorge zögert.
 */


/**
 * Fragt vor dem Stilllegen nach - mit Eingabe des Modellnamens.
 *
 * Ein einfaches Bestätigen wäre zu schnell weggeklickt. Wer den Namen tippen
 * muss, hat gelesen, um welches Modell es geht - und das ist bei fünf ähnlich
 * benannten Fachmodellen der Punkt, auf den es ankommt.
 *
 * Rückgängig geht es über "Wieder aufnehmen" - das steht auch im Dialog, damit
 * niemand aus Sorge zögert.
 */
function StilllegenDialog({
  modell, abbrechen, bestaetigen,
}: {
  modell: Modell | null;
  abbrechen: () => void;
  bestaetigen: () => Promise<void>;
}) {
  const [laeuft, setLaeuft] = useState(false);

  return (
    <ConfirmDialog
      offen={modell !== null}
      art="warnung"
      titel={`${modell?.name ?? ''} stilllegen?`}
      // Ein einfaches Bestätigen wäre zu schnell weggeklickt. Wer den Namen
      // tippen muss, hat gelesen, um welches Modell es geht — und das ist bei
      // fünf ähnlich benannten Fachmodellen der Punkt, auf den es ankommt.
      bestaetigungswort={modell?.name}
      bestaetigenText={laeuft ? 'wird stillgelegt …' : 'Stilllegen'}
      laeuft={laeuft}
      beiBestaetigen={async () => { setLaeuft(true); await bestaetigen(); setLaeuft(false); }}
      beiAbbrechen={abbrechen}
    >
      <div style={{ background: 'var(--flaeche)', padding: '12px 14px',
                    borderRadius: 3, fontSize: 13, lineHeight: 1.7 }}>
        <div><strong>Was geschieht:</strong></div>
        <div className="schwach">
          Das Modell verschwindet aus der Liste und lässt sich nicht mehr
          beauftragen. Zeitpläne, die alle Modelle umfassen, lassen es aus.
        </div>

        <div style={{ marginTop: 10 }}><strong>Was bleibt:</strong></div>
        <div className="schwach">
          Alle bisherigen Aufträge und ihre Dateien. Das Modell in Revit und
          in ACC bleibt unberührt — hier wird nichts gelöscht.
        </div>

        <div style={{ marginTop: 10 }}><strong>Rückgängig:</strong></div>
        <div className="schwach">
          Jederzeit über „Wieder aufnehmen" unterhalb der Modelliste.
        </div>
      </div>
    </ConfirmDialog>
  );
}

/**
 * Die bisherigen Exporte eines Modells, nach Art gegliedert.
 *
 * Der jüngste erfolgreiche Lauf je Art ist gekennzeichnet - danach sucht man
 * in der Regel, und die Liste wird mit der Zeit lang.
 */


/**
 * Die bisherigen Exporte eines Modells, nach Art gegliedert.
 *
 * Der jüngste erfolgreiche Lauf je Art ist gekennzeichnet - danach sucht man
 * in der Regel, und die Liste wird mit der Zeit lang.
 */
export function Exportbaum({
  api, projektId, modell,
}: {
  api: ApiClient;
  projektId: string;
  modell: Modell;
}) {
  const [gruppen, setGruppen] = useState<Exportgruppe[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offeneArten, setOffeneArten] = useState<Set<number>>(new Set());

  useEffect(() => {
    api.modellExporte(projektId, modell.id)
      .then((g) => {
        setGruppen(g);
        // Die erste Art gleich aufklappen - ein leerer Baum sieht aus, als
        // wäre nichts da.
        if (g.length > 0) setOffeneArten(new Set([g[0].kind]));
      })
      .catch(setFehler);
  }, [api, projektId, modell.id]);

  if (fehler) {
    return (
      <ErrorState
        meldung="Die bisherigen Exporte konnten nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!gruppen) return <LoadingState text="Exporte werden geladen …" zeilen={5} />;

  if (gruppen.length === 0) {
    return (
      <EmptyState
        titel="Für dieses Modell wurde noch nichts exportiert."
        erklaerung={'Ein Export entsteht aus einem Auftrag — über die Knöpfe in der '
          + 'Spalte „Beauftragen".'}
      />
    );
  }

  const umschalten = (kind: number) => {
    const neu = new Set(offeneArten);
    if (neu.has(kind)) neu.delete(kind); else neu.add(kind);
    setOffeneArten(neu);
  };

  return (
    <div style={{ maxWidth: 900 }}>
      {gruppen.map((g) => (
        <div key={g.kind} style={{ marginTop: 6 }}>
          <button
            onClick={() => umschalten(g.kind)}
            style={{
              border: 'none', background: 'transparent', padding: '4px 0',
              display: 'flex', alignItems: 'center', gap: 8, width: '100%',
              textAlign: 'left',
            }}
          >
            <span style={{ width: 10, color: 'var(--text-sehr-schwach)', fontSize: 11 }}>
              {offeneArten.has(g.kind) ? '▾' : '▸'}
            </span>
            <span style={{ color: 'var(--akzent)' }}>{ArtIcon(g.kind)}</span>
            <strong style={{ fontSize: 13 }}>{ArtName(g.kind)}</strong>
            <span className="sehr-schwach">
              {g.auftraege.length} {g.auftraege.length === 1 ? 'Auftrag' : 'Aufträge'}
            </span>
          </button>

          {offeneArten.has(g.kind) && (
            <DataTable
              spalten={[
                {
                  schluessel: 'zeit', titel: 'Zeitpunkt', art: 'datum', breite: '150px',
                  wert: (a: Exportgruppe['auftraege'][number]) => a.createdAt,
                  inhalt: (a) => new Date(a.createdAt).toLocaleString('de-DE', {
                    day: '2-digit', month: '2-digit', year: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                  }),
                },
                {
                  schluessel: 'nummer', titel: 'Auftrag', breite: '180px',
                  immerSichtbar: true,
                  wert: (a: Exportgruppe['auftraege'][number]) => a.jobNumber,
                  inhalt: (a) => (
                    <>
                      <Link to={`/auftraege/${a.id}`}>{a.jobNumber}</Link>
                      {a.jobNumber === g.aktuell && (
                        <> <StatusBadge zustand="gut" text="aktuell" klein /></>
                      )}
                    </>
                  ),
                },
                {
                  // AUF2: Text und Zustand wie in „Aufträge“ und im Auftragsdetail
                  // (auftragsstatus.ts). Bis dahin stand jeder nicht beendete Auftrag
                  // als „läuft“ - auch ein wartender (0) - und jeder nicht
                  // erfolgreiche als „gescheitert“, auch ein abgebrochener.
                  schluessel: 'status', titel: 'Status', art: 'status', breite: '140px',
                  wert: (a: Exportgruppe['auftraege'][number]) => auftragStatusText(a.status),
                  inhalt: (a) => (
                    <StatusBadge
                      zustand={auftragZustand(a.status)}
                      text={auftragStatusText(a.status)}
                      klein
                      titel={auftragBeendet(a.status) && auftragStatusName(a.status) !== 'Succeeded'
                        ? a.failureReason ?? undefined
                        : undefined}
                    />
                  ),
                },
                {
                  schluessel: 'dateien', titel: 'Dateien', sortierbar: false,
                  wert: (a: Exportgruppe['auftraege'][number]) =>
                    a.dateien.map((d) => d.fileName).join(', '),
                  inhalt: (a) => (a.dateien.length === 0
                    ? <span className="sehr-schwach">—</span>
                    : a.dateien.map((d) => (
                      <button
                        key={d.id}
                        className="m-knopf"
                        title={`${d.fileName} (${(d.sizeBytes / 1048576).toFixed(1)} MB)`}
                        onClick={() => api.dateiLaden(
                          `/api/jobs/${a.id}/artifacts/${d.id}/download`, d.fileName)}
                        style={{ marginRight: 4 }}
                      >
                        <IconHerunterladen size={13} />
                        {d.fileName.length > 28
                          ? `${d.fileName.slice(0, 25)}…`
                          : d.fileName}
                      </button>
                    ))),
                },
              ]}
              zeilen={g.auftraege}
              schluessel={(a) => a.id}
              suche={g.auftraege.length > 8}
              spaltenwahl={false}
              dicht
            />
          )}
        </div>
      ))}
    </div>
  );
}


function ArtName(kind: number): string {
  switch (kind) {
    case 1:  return 'IFC';
    case ART_IFC_AUS_DATENKERN: return 'IFC aus Datenkern';
    case ART_ENGINE_AUFNAHME: return 'Revit-Import (Engine)';
    case 2:  return 'PDF';
    case 3:  return 'DWG';
    case 10: return 'Raumdaten gelesen';
    case 11: return 'Raumdaten geschrieben';
    case 20: return 'Türdaten gelesen';
    case 21: return 'Türdaten geschrieben';
    case 30: return 'Lüftung gelesen';
    case 31: return 'Lüftung geschrieben';
    default: return `Art ${kind}`;
  }
}


function ArtIcon(kind: number) {
  switch (kind) {
    case 1:  return <IconIfc size={14} />;
    case ART_IFC_AUS_DATENKERN: return <IconIfc size={14} />;
    case 2:  return <IconPdf size={14} />;
    case 3:  return <IconDwg size={14} />;
    case 10: return <IconRaumLesen size={14} />;
    case 11: return <IconRaumSchreiben size={14} />;
    case 20: return <IconTuer size={14} />;
    case 21: return <IconTuertyp size={14} />;
    case 30: return <IconLueftung size={14} />;
    case 31: return <IconKennung size={14} />;
    default: return null;
  }
}

/**
 * Das Zeitlimit je Modell.
 *
 * Ein Sammelmodell mit fünfzehn Verknüpfungen braucht für das Nachladen allein
 * fast eine Stunde - mit den voreingestellten neunzig Minuten bricht der Export
 * dann ab, obwohl er nur länger dauert.
 *
 * Ein gemeinsames Limit hochzusetzen wäre die schlechtere Wahl: Ein hängendes
 * Revit blockierte die Maschine dann bei jedem Modell entsprechend lange.
 */


/**
 * Das Zeitlimit je Modell.
 *
 * Ein Sammelmodell mit fünfzehn Verknüpfungen braucht für das Nachladen allein
 * fast eine Stunde - mit den voreingestellten neunzig Minuten bricht der Export
 * dann ab, obwohl er nur länger dauert.
 *
 * Ein gemeinsames Limit hochzusetzen wäre die schlechtere Wahl: Ein hängendes
 * Revit blockierte die Maschine dann bei jedem Modell entsprechend lange.
 */
function Zeitlimitfeld({
  api, projektId, modell, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  modell: Modell;
  neuLaden: () => void;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [wert, setWert] = useState(String(modell.timeoutMinutes ?? ''));

  const speichern = async () => {
    const zahl = parseInt(wert.trim(), 10);
    const neu = Number.isFinite(zahl) && zahl > 0 ? zahl : null;

    if (neu === modell.timeoutMinutes) { setBearbeiten(false); return; }

    try {
      // 0 setzt serverseitig auf die Vorgabe zurück.
      await api.modellAendern(projektId, modell.id, { timeoutMinutes: neu ?? 0 });
      setBearbeiten(false);
      neuLaden();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
      setWert(String(modell.timeoutMinutes ?? ''));
      setBearbeiten(false);
    }
  };

  if (!bearbeiten) {
    return (
      <button
        onClick={() => { setWert(String(modell.timeoutMinutes ?? '')); setBearbeiten(true); }}
        title={modell.timeoutMinutes
          ? `${modell.timeoutMinutes} Minuten - zum Ändern anklicken`
          : 'Vorgabe: 90 Minuten - zum Ändern anklicken'}
        style={{
          border: '1px solid transparent', background: 'transparent',
          padding: '3px 6px', width: '100%', textAlign: 'right', fontSize: 13,
          color: modell.timeoutMinutes ? 'var(--akzent)' : 'var(--text-sehr-schwach)',
          fontVariantNumeric: 'tabular-nums',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--linie)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
      >
        {modell.timeoutMinutes ?? 90}
      </button>
    );
  }

  return (
    <input
      value={wert}
      autoFocus
      onChange={(e) => setWert(e.target.value.replace(/[^0-9]/g, ''))}
      onBlur={speichern}
      onKeyDown={(e) => {
        if (e.key === 'Enter') speichern();
        if (e.key === 'Escape') {
          setWert(String(modell.timeoutMinutes ?? ''));
          setBearbeiten(false);
        }
      }}
      placeholder="90"
      title="Leer lassen für die Vorgabe von 90 Minuten"
      style={{
        width: '100%', padding: '3px 6px', fontSize: 13, textAlign: 'right',
        fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}

/**
 * Das Gewerk in der Zeile bearbeiten.
 *
 * Ein Klick genügt - ein eigener Dialog wäre für ein einzelnes Feld zu viel.
 * Gespeichert wird beim Verlassen oder mit der Eingabetaste; Escape verwirft.
 */


/**
 * Das Gewerk in der Zeile bearbeiten.
 *
 * Ein Klick genügt - ein eigener Dialog wäre für ein einzelnes Feld zu viel.
 * Gespeichert wird beim Verlassen oder mit der Eingabetaste; Escape verwirft.
 */
function Gewerkfeld({
  api, projektId, modell, neuLaden,
}: {
  api: ApiClient;
  projektId: string;
  modell: Modell;
  neuLaden: () => void;
}) {
  const [bearbeiten, setBearbeiten] = useState(false);
  const [wert, setWert] = useState(modell.discipline ?? '');
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    const neu = wert.trim();

    if (neu === (modell.discipline ?? '')) {
      setBearbeiten(false);
      return;
    }

    setLaeuft(true);
    try {
      await api.modellAendern(projektId, modell.id, { discipline: neu });
      setBearbeiten(false);
      neuLaden();
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
      setWert(modell.discipline ?? '');
      setBearbeiten(false);
    } finally {
      setLaeuft(false);
    }
  };

  if (!bearbeiten) {
    return (
      <button
        onClick={() => { setWert(modell.discipline ?? ''); setBearbeiten(true); }}
        title="Zum Bearbeiten anklicken"
        style={{
          border: '1px solid transparent', background: 'transparent',
          padding: '3px 6px', textAlign: 'left', width: '100%',
          color: modell.discipline ? 'var(--text-schwach)' : 'var(--text-sehr-schwach)',
          fontSize: 13,
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--linie)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'transparent'; }}
      >
        {modell.discipline || '—'}
      </button>
    );
  }

  return (
    <input
      value={wert}
      autoFocus
      disabled={laeuft}
      onChange={(e) => setWert(e.target.value)}
      onBlur={speichern}
      onKeyDown={(e) => {
        if (e.key === 'Enter') speichern();
        // Escape verwirft - wer sich vertippt hat, kommt so heraus, ohne den
        // alten Wert überschrieben zu haben.
        if (e.key === 'Escape') { setWert(modell.discipline ?? ''); setBearbeiten(false); }
      }}
      style={{ width: '100%', padding: '3px 6px', fontSize: 13 }}
      placeholder="Gewerk"
    />
  );
}


/**
 * Markiert, welche Daten in diesem Modell liegen.
 *
 * Bei einem Projekt mit fünf Fachmodellen ist die Zuordnung sonst unbestimmt:
 * Die Raumdaten liegen in TGAA_003, die Türdaten in ELEA_001. Eine Aufnahme auf
 * dem falschen Modell liefert nichts - und das fällt erst nach zehn Minuten
 * Wartezeit auf.
 */


/**
 * Auftraege direkt aus der Modellzeile beauftragen.
 *
 * Verwendet wird das zuletzt freigegebene Profil, das zur Revit-Fassung des
 * Modells passt. Gibt es mehrere, waehlt eine Liste - meist ist es aber genau
 * eines, und dann soll ein Klick genuegen.
 */
export function Modellaktionen({
  api, projektId, modell, ifcAusDatenkern, engineEingang,
}: {
  api: ApiClient;
  projektId: string;
  modell: Modell;
  /** IFC-3d: Schalter Bim:IfcExport laut Server (ProjektDetail.ifcAusDatenkern). */
  ifcAusDatenkern?: boolean;
  /** MP-6: Schalter Bim:EngineEingang laut Server (ProjektDetail.engineEingang). */
  engineEingang?: boolean;
}) {
  const [profile, setProfile] = useState<ProfilEintrag[] | null>(null);
  const [laeuft, setLaeuft] = useState<number | null>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [nachfrage, setNachfrage] = useState<{ art: number; name: string } | null>(null);

  useEffect(() => {
    api.profile(projektId, modell.revitVersion)
      .then(setProfile)
      .catch(() => setProfile([]));
  }, [api, projektId, modell.revitVersion]);

  const versionen = (profile ?? []).flatMap((p) =>
    p.versions.map((v) => ({ id: v.id, text: `${p.name} ${v.version}` })));

  const beauftragen = (art: number, name: string) => {
    if (versionen.length === 0) {
      setMeldung('Kein freigegebenes Profil für Revit ' + modell.revitVersion);
      setTimeout(() => setMeldung(null), 4000);
      return;
    }

    setNachfrage({ art, name });
  };

  const ausfuehren = async (art: number, name: string, empfaenger: string) => {
    setNachfrage(null);
    setLaeuft(art);
    setMeldung(null);

    try {
      const auftrag = await api.auftragAnlegen({
        projectId: projektId,
        exportProfileVersionId: versionen[0].id,
        modelIds: [modell.id],
        kinds: [art],
        notifyEmails: empfaenger
          .split(/[;,]/)
          .map((e) => e.trim())
          .filter((e) => e.length > 0),
      });

      setMeldung(`${name}: ${auftrag.jobNumber} angelegt`);
      setTimeout(() => setMeldung(null), 8000);
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
      // K3 (F447): Kann keine Verarbeitungsmaschine die Revit-Version des Modells, bleibt die Meldung stehen - sie
      // erklärt eine Einrichtungslücke samt Abhilfe, das liest niemand in acht Sekunden.
      if (!(e instanceof ApiFehler && e.code === FEHLER_REVIT_VERSION_OHNE_MASCHINE))
        setTimeout(() => setMeldung(null), 8000);
    } finally {
      setLaeuft(null);
    }
  };

  // Die Raumauftraege nur, wenn das Modell als Quelle markiert ist. Sonst
  // liefert eine Aufnahme nichts, und beim Rueckschreiben landeten die Werte im
  // falschen Modell.
  // Raum, Tuer und Lueftung stehen nicht mehr hier, sondern im Kopf der
  // jeweiligen Fachseite. Wer im Raumbuch arbeitet, will von dort aus lesen
  // und schreiben - und nicht einen Reiter zurueck und die richtige Zeile
  // suchen. In der Uebersicht bleiben die Ausgabeformate.
  // IFC-3d (F248): „IFC aus Datenkern“ (Art 200) nur, wenn der Server den
  // Schalter Bim:IfcExport meldet (ifcDatenkern.ts).
  // MP-6 (F324): „Revit-Import (Engine)“ (Art 161) nur, wenn der Server den Schalter Bim:EngineEingang meldet
  // (engineEingang.ts); gesperrt mit Grund, wenn das Modell keine hochgeladene Datei hat.
  const engine = engineAktion(engineEingang);
  const hindernis = engine ? engineHindernis(modell) : null;
  const aktionen = [...exportaktionen(ifcAusDatenkern), ...(engine ? [engine] : [])].map((a) => ({
    ...a,
    aktiv: a.art === ART_ENGINE_AUFNAHME ? hindernis === null : true,
    titel: a.art === ART_ENGINE_AUFNAHME && hindernis ? `${a.titel} - ${hindernis}` : a.titel,
    icon: a.art === ART_ENGINE_AUFNAHME
      ? <span style={{ fontSize: 10, fontWeight: 600 }}>Engine</span>
      : a.art === ART_IFC_AUS_DATENKERN
      ? (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          <IconIfc size={15} />
          <span style={{ fontSize: 10 }}>Kern</span>
        </span>
      )
      : a.art === 2 ? <IconPdf size={15} />
        : a.art === 3 ? <IconDwg size={15} />
          : <IconIfc size={15} />,
  }));

  return (
    <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
      {aktionen.map((a) => (
        <button
          key={a.art}
          className="nur-icon"
          title={a.titel}
          aria-label={a.art === ART_ENGINE_AUFNAHME ? a.name : undefined}
          data-art={a.art}
          disabled={laeuft !== null || !modell.isActive || !a.aktiv}
          onClick={() => beauftragen(a.art, a.name)}
          style={{
            padding: '5px 7px',
            opacity: laeuft === a.art ? 0.4 : 1,
            // Das Rueckschreiben abgesetzt: Es ist die einzige Aktion, die das
            // Modell veraendert.
            marginLeft: 0,
            borderColor: a.art === 11 ? 'var(--warn-rand, #e6c060)' : undefined,
          }}
        >
          {a.icon}
        </button>
      ))}

      {meldung && (
        // K3: eine Meldung mit Abhilfe (Absatz) bricht um, statt über die Zeile hinauszulaufen.
        <span className="sehr-schwach" style={{ marginLeft: 8, whiteSpace: meldung.includes('\n') ? 'pre-line' : 'nowrap' }}>
          {meldung}
        </span>
      )}

      {nachfrage && (
        <Auftragsdialog
          modellname={modell.name}
          art={nachfrage.art}
          artname={nachfrage.name}
          abbrechen={() => setNachfrage(null)}
          starten={(e) => ausfuehren(nachfrage.art, nachfrage.name, e)}
        />
      )}
    </div>
  );
}

/**
 * Fragt vor dem Beauftragen nach zusätzlichen Empfängern.
 *
 * Der Auftraggeber bekommt die Nachricht ohnehin - hier geht es um die
 * Kollegen, die das Ergebnis auch brauchen. Beim Rückschreiben tritt die
 * Warnung an diese Stelle: Dort entsteht keine Datei, sondern eine Änderung
 * am Modell.
 */


/**
 * Fragt vor dem Beauftragen nach zusätzlichen Empfängern.
 *
 * Der Auftraggeber bekommt die Nachricht ohnehin - hier geht es um die
 * Kollegen, die das Ergebnis auch brauchen. Beim Rückschreiben tritt die
 * Warnung an diese Stelle: Dort entsteht keine Datei, sondern eine Änderung
 * am Modell.
 */
function Auftragsdialog({
  modellname, art, artname, abbrechen, starten,
}: {
  modellname: string;
  art: number;
  artname: string;
  abbrechen: () => void;
  starten: (empfaenger: string) => void;
}) {
  const [empfaenger, setEmpfaenger] = useState('');
  // Beide Rueckschreibarten veraendern das Modell.
  const schreibt = art === 11 || art === 21;

  return (
    <ConfirmDialog
      offen
      art={schreibt ? 'warnung' : 'standard'}
      titel={`${artname} beauftragen`}
      text={modellname}
      bestaetigenText={schreibt ? 'Schreiben' : 'Beauftragen'}
      beiBestaetigen={() => starten(empfaenger)}
      beiAbbrechen={abbrechen}
    >
      {schreibt ? (
        <div className="hinweis warnung">
          <span className="hinweis-zeichen">!</span>
          <div>
            Die gepflegten {art === 21 ? 'Türdaten' : 'Raumdaten'} werden ins Modell
            geschrieben und mit der Zentraldatei abgeglichen. Rückgängig geht das
            nur über eine frühere Fassung in ACC.
          </div>
        </div>
      ) : (
        <Feld
          beschriftung="Ergebnis auch an"
          wert={empfaenger}
          beiAenderung={(w) => setEmpfaenger(w as string)}
          platzhalter="niemand"
          hinweis="Mehrere durch Komma. Sie selbst erhalten die Nachricht in jedem Fall."
        />
      )}
    </ConfirmDialog>
  );
}

function ModellAnlegen({
  api, projektId, fertig,
}: {
  api: ApiClient;
  projektId: string;
  fertig: () => void;
}) {
  const [name, setName] = useState('');
  const [gewerk, setGewerk] = useState('');
  const [quelle, setQuelle] = useState(2);
  const [projektGuid, setProjektGuid] = useState('');
  const [modellGuid, setModellGuid] = useState('');
  const [speicherpfad, setSpeicherpfad] = useState('');
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.modellAnlegen(projektId, {
        name: name.trim(),
        discipline: gewerk.trim() || null,
        revitVersion: 2025,
        source: quelle,
        accProjectGuid: quelle === 2 ? projektGuid.trim() || null : null,
        accModelGuid: quelle === 2 ? modellGuid.trim() || null : null,
        storageKey: quelle === 1 ? speicherpfad.trim() || null : null,
      });
      fertig();
    } catch (e) {
      setFehler(e);
      setLaeuft(false);
    }
  };

  const vollstaendig = name.trim() && (quelle === 1 || (projektGuid.trim() && modellGuid.trim()));

  return (
    <FormSection
      titel="Neues Modell"
      spalten={2}
      fuss={(
        <button className="m-knopf m-knopf-haupt" onClick={speichern}
                disabled={!vollstaendig || laeuft}>
          {laeuft ? 'wird angelegt …' : 'Anlegen'}
        </button>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Das Modell konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Modellname"
        wert={name}
        beiAenderung={(w) => setName(w as string)}
        platzhalter="TP1_TUV_MX_SDM_TGAA_001"
        pflicht
      />
      <Feld
        beschriftung="Gewerk"
        wert={gewerk}
        beiAenderung={(w) => setGewerk(w as string)}
        platzhalter="TGA"
      />
      <Feld
        art="auswahl"
        beschriftung="Herkunft"
        wert={String(quelle)}
        beiAenderung={(w) => setQuelle(Number(w))}
        optionen={[
          { wert: '2', beschriftung: 'Autodesk Construction Cloud' },
          { wert: '1', beschriftung: 'Datei im Objektspeicher' },
        ]}
      />

      {quelle === 2 ? (
        <>
          <Feld
            beschriftung="ACC-Projektkennung"
            wert={projektGuid}
            beiAenderung={(w) => setProjektGuid(w as string)}
            platzhalter="23dad5f8-8259-49f2-a184-c62468932672"
            pflicht
            hinweis={'Steht in Revit unter Zusatzmodule → TuV → Cloud-Kennungen, wenn '
              + 'das Modell aus der Cloud geöffnet ist. In der ACC-Oberfläche ist sie '
              + 'nicht sichtbar.'}
          />
          <Feld
            beschriftung="ACC-Modellkennung"
            wert={modellGuid}
            beiAenderung={(w) => setModellGuid(w as string)}
            platzhalter="88092f3d-218a-4c46-982e-50fe6dd7d4b3"
            pflicht
          />
        </>
      ) : (
        <Feld
          beschriftung="Pfad im Speicher"
          wert={speicherpfad}
          beiAenderung={(w) => setSpeicherpfad(w as string)}
          platzhalter="models/2026-041/modell.rvt"
        />
      )}
    </FormSection>
  );
}

// --- Lagekorrektur ----------------------------------------------------------
